// Stage 2B — reusable Leaflet + OpenStreetMap map (free public tiles).
// Single-location (business detail) or multi-marker (Discover results) use.
import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix Leaflet's default marker icon paths (broken under bundlers like CRA).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Fit the map to all markers when there are several.
const FitBounds = ({ points }) => {
  const map = useMap();
  React.useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
};

/**
 * @param {Array<{id?:string, lat:number, lng:number, title?:string}>} markers
 * @param {number} [height=320]
 * @param {(id:string)=>void} [onMarkerClick]
 */
const BusinessMap = ({ markers = [], height = 320, zoom = 14, onMarkerClick }) => {
  const points = (markers || []).filter(
    (m) => typeof m.lat === 'number' && typeof m.lng === 'number'
  );
  if (points.length === 0) return null;
  const center = [points[0].lat, points[0].lng];

  return (
    <div
      className="rounded-xl overflow-hidden border border-gray-200 shadow-sm"
      style={{ height }}
      data-testid="business-map"
    >
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBounds points={points} />
        {points.map((m, i) => (
          <Marker
            key={m.id || i}
            position={[m.lat, m.lng]}
            eventHandlers={onMarkerClick && m.id ? { click: () => onMarkerClick(m.id) } : undefined}
          >
            {m.title && (
              <Popup>
                <span className="font-medium">{m.title}</span>
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default BusinessMap;
